/**
 * Televendas (redesign admin, Fase 3): montagem de pedido pelo operador. Puro e
 * sem I/O.
 *
 * O pedido nasce pelo MESMO contrato dos demais canais
 * (`POST /api/platform/orders`, `CreatePlatformOrderInput`): a Loja não entra
 * aqui (vem da projeção do checkout) e o servidor continua sendo a autoridade
 * de validação (duplicidade, vínculo com conversa/cliente). Esta camada só
 * impede o envio óbvio-errado e traduz o formulário em centavos inteiros.
 *
 * Preço vem da oferta vendável vigente do catálogo (`sellableOffer`); produto
 * sem oferta não entra no carrinho (nada a vender). Frete nunca é inventado: o
 * operador informa o combinado, inclusive 0.
 *
 * Link de pagamento: não há contrato de geração de cobrança para venda de
 * canal; a tela declara a pendência em vez de simular um link.
 */
import type { CreatePlatformOrderInput } from "../api/platform-client.js";
import type { PlatformCatalogProduct } from "../api/types.js";

/** Origens que o operador pode registrar: a Loja é projetada e o ML chega pelo canal. */
export const TELEVENDAS_ORIGINS = ["whatsapp", "manual"] as const;
export type TelevendasOrigin = (typeof TELEVENDAS_ORIGINS)[number];

export interface CartLine {
  productId: string;
  offerId: string;
  name: string;
  quantity: number;
  unitPriceCents: number;
  /** Estoque da oferta no momento em que entrou no carrinho (aviso, não trava). */
  stock: number;
}

/** "12,50", "1.234,5" ou "12.5" → centavos; vazio/negativo/inválido → `null`. */
export function parseBrlToCents(text: string): number | null {
  const raw = text.trim().replace(/\s/g, "").replace(/^R\$/i, "");
  if (!raw) return null;
  // Com vírgula, o ponto é separador de milhar; sem vírgula, o ponto é decimal.
  const normalized = raw.includes(",") ? raw.replace(/\./g, "").replace(",", ".") : raw;
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  return Math.round(Number(normalized) * 100);
}

/** Entra produto no carrinho (soma quantidade se a oferta já está); sem oferta vendável não entra. */
export function addToCart(cart: CartLine[], product: PlatformCatalogProduct): CartLine[] {
  const offer = product.sellableOffer;
  if (!offer) return cart;
  const existing = cart.find((line) => line.offerId === offer.id);
  if (existing) {
    return cart.map((line) => (line.offerId === offer.id ? { ...line, quantity: line.quantity + 1 } : line));
  }
  return [
    ...cart,
    {
      productId: product.productId,
      offerId: offer.id,
      name: product.name,
      quantity: 1,
      unitPriceCents: offer.priceCents,
      stock: offer.stock
    }
  ];
}

/** Quantidade ≤ 0 remove a linha. */
export function setQuantity(cart: CartLine[], offerId: string, quantity: number): CartLine[] {
  if (!Number.isInteger(quantity) || quantity <= 0) return cart.filter((line) => line.offerId !== offerId);
  return cart.map((line) => (line.offerId === offerId ? { ...line, quantity } : line));
}

export function cartSubtotalCents(cart: CartLine[]): number {
  return cart.reduce((sum, line) => sum + line.unitPriceCents * line.quantity, 0);
}

/** Linhas que pedem mais do que o estoque da oferta — aviso ao operador, o servidor decide. */
export function overStockLines(cart: CartLine[]): CartLine[] {
  return cart.filter((line) => line.quantity > line.stock);
}

export interface TelevendasForm {
  origin: TelevendasOrigin;
  externalId: string;
  name: string;
  email: string;
  phone: string;
  /** Texto digitado pelo operador (ex.: "25,90"). */
  shipping: string;
  cart: CartLine[];
  /** Origem de aquisição (0062): slug de `sales_origin_options`. Vazio = não escolhida. */
  acquisitionOrigin?: string;
  /** Estratégia/campanha em texto livre (ex.: "Black Friday"). */
  acquisitionStrategy?: string;
  /** Quem fechou a venda. */
  responsibleName?: string;
}

export type TelevendasField = "externalId" | "name" | "email" | "phone" | "shipping" | "cart" | "acquisitionOrigin";

export interface BuildOrderOptions {
  /**
   * Exige escolher a origem. A tela liga isto quando o cadastro de origens carregou; sem o
   * cadastro (rota não publicada) a venda continua registrável, sem origem, em vez de travar.
   */
  requireAcquisitionOrigin?: boolean;
}

export type BuildOrderResult =
  | { ok: true; input: CreatePlatformOrderInput }
  | { ok: false; errors: Partial<Record<TelevendasField, string>> };

export function buildOrderInput(form: TelevendasForm, options: BuildOrderOptions = {}): BuildOrderResult {
  const errors: Partial<Record<TelevendasField, string>> = {};
  if (!form.externalId.trim()) errors.externalId = "Informe o identificador da venda no canal.";
  if (!form.name.trim()) errors.name = "Informe o nome do cliente.";
  if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) errors.email = "Informe um e-mail válido.";
  if (form.phone.replace(/\D/g, "").length < 10) errors.phone = "Informe o telefone com DDD.";
  const shippingCents = parseBrlToCents(form.shipping);
  if (shippingCents === null) errors.shipping = "Informe o frete combinado (use 0 se não houver).";
  if (form.cart.length === 0) errors.cart = "Adicione ao menos um produto.";
  if (options.requireAcquisitionOrigin && !form.acquisitionOrigin?.trim()) errors.acquisitionOrigin = "Informe de onde veio este contato (use \"Não identificado\" se não souber).";
  if (Object.keys(errors).length > 0 || shippingCents === null) return { ok: false, errors };
  return {
    ok: true,
    input: {
      origin: form.origin,
      externalId: form.externalId.trim(),
      customer: { name: form.name.trim(), email: form.email.trim(), phone: form.phone.replace(/\D/g, "") },
      items: form.cart.map((line) => ({
        productId: line.productId,
        offerId: line.offerId,
        quantity: line.quantity,
        unitPriceCents: line.unitPriceCents
      })),
      shippingCents,
      ...(form.acquisitionOrigin?.trim() ? { acquisitionOrigin: form.acquisitionOrigin.trim() } : {}),
      ...(form.acquisitionStrategy?.trim() ? { acquisitionStrategy: form.acquisitionStrategy.trim() } : {}),
      ...(form.responsibleName?.trim() ? { responsibleName: form.responsibleName.trim() } : {})
    }
  };
}

/** Filtro do seletor de produto: nome ou SKU, sem acento/caixa; só quem tem oferta vendável. */
export function sellableProducts(products: PlatformCatalogProduct[], query: string): PlatformCatalogProduct[] {
  const fold = (text: string) => text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
  const needle = fold(query);
  return products.filter(
    (product) => product.sellableOffer !== null && (!needle || fold(product.name).includes(needle) || fold(product.sku).includes(needle))
  );
}
